import { useCallback, useEffect, useState } from 'react';
import api, { onUnauthorized } from './api/client.js';
import { useLocation, navigate } from './lib/router.js';
import { useSettings } from './state/useSettings.js';
import Login from './pages/Login.jsx';
import Day from './pages/Day.jsx';
import Admin from './pages/Admin.jsx';
import Header from './components/Header.jsx';
import Toast from './components/Toast.jsx';

const DEFAULT_TITLE = 'سجلّ مواعيد المحافظ';
const DEFAULT_ORG = 'محافظة السويداء';

export default function App() {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);
  const [displayError, setDisplayError] = useState('');
  const [toast, setToast] = useState(null);

  const location = useLocation();
  const {
    settings,
    hasLetterhead,
    letterheadSource,
    reloadSettings,
    setSettings,
    setHasLetterhead,
    setLetterheadSource,
  } = useSettings(Boolean(user));

  const showToast = useCallback((message, tone = 'info') => {
    setToast({ message, tone, key: Date.now() });
  }, []);

  /* Startup: resume an existing session, or exchange a display link for one. */
  useEffect(() => {
    let cancelled = false;

    async function boot() {
      const params = new URLSearchParams(window.location.search);
      const displayToken = params.get('token');

      if (window.location.pathname === '/display' && displayToken) {
        try {
          const result = await api.loginWithDisplayToken(displayToken);
          if (cancelled) return;
          setUser(result.user);
          // Drop the token from the address bar so it is not left visible on an
          // unattended screen or captured in a screenshot. The session cookie
          // now carries the authorization.
          navigate('/', { replace: true });
        } catch {
          if (!cancelled) setDisplayError('رابط الشاشة غير صالح أو تم إلغاؤه');
        } finally {
          if (!cancelled) setBooting(false);
        }
        return;
      }

      try {
        const result = await api.me();
        if (!cancelled) setUser(result.user);
      } catch {
        // No valid session; the login screen renders.
      } finally {
        if (!cancelled) setBooting(false);
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Any 401 from any request means the session ended. */
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser((current) => {
          if (current) showToast('انتهت الجلسة. يرجى تسجيل الدخول من جديد.', 'error');
          return null;
        });
      }),
    [showToast]
  );

  const handleSignOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Even if the request fails, clear locally so the screen is not stuck.
    }
    setUser(null);
    navigate('/', { replace: true });
  }, []);

  if (booting) {
    return <div className="loading-state">...جاري التحميل</div>;
  }

  if (!user) {
    return (
      <>
        {displayError && (
          <div className="login-page">
            <div className="login-card">
              <div className="error-state">{displayError}</div>
            </div>
          </div>
        )}
        {!displayError && (
          <Login
            onSignedIn={setUser}
            appTitle={settings?.app_title || DEFAULT_TITLE}
            orgName={settings?.org_name || DEFAULT_ORG}
          />
        )}
      </>
    );
  }

  const isAdminRoute = location.pathname === '/admin';

  return (
    <div className="app-shell">
      <Header
        user={user}
        settings={settings}
        onSignOut={handleSignOut}
        isAdminRoute={isAdminRoute}
      />

      <main className="site-main">
        {isAdminRoute ? (
          <Admin
            user={user}
            settings={settings}
            hasLetterhead={hasLetterhead}
            letterheadSource={letterheadSource}
            onSettingsChanged={(next, letterhead, source) => {
              setSettings(next);
              if (letterhead !== undefined) setHasLetterhead(letterhead);
              if (source !== undefined) setLetterheadSource(source);
            }}
            reloadSettings={reloadSettings}
            showToast={showToast}
          />
        ) : (
          <Day
            user={user}
            settings={settings}
            hasLetterhead={hasLetterhead}
            showToast={showToast}
            onSettingsChanged={reloadSettings}
          />
        )}
      </main>

      <Toast
        key={toast?.key}
        message={toast?.message}
        tone={toast?.tone}
        onDismiss={() => setToast(null)}
      />
    </div>
  );
}
